import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Mercedes-Benz 300 SL "Gullwing" (W198, 1954) — the classic 1950s sports
// coupe at true scale (1 unit = 1 metre): length ~4.52, width 1.79, height
// 1.30, wheelbase 2.40, on wire wheels (r ~0.33). Nose is +Z. A LOW, WIDE and
// curvaceous silver coupe: a long curved bonnet with a twin power bulge, a
// huge chrome oval grille dominated by a central three-point star, a small
// wraparound greenhouse set well back over a short fastback tail, chrome
// "eyebrow" brows arching over every wheel arch, louvred chrome fender gills
// behind the front wheels, round headlamps faired into the fender fronts,
// chrome bumpers with overriders front and rear, and a red interior hint
// glimpsed through the glass. Body panels carry the "paint" recolour slot
// (iconic silver); `drive` spins the wheels and `hazards` blinks four
// indicators.

const PAINT = "#c8ccce"; // classic silver — the "paint" slot default
const CHROME = "#dfe3e7"; // bright chrome — brows, grille ring, bumpers (reads over the silver)
const TRIM = "#17181b"; // gloss-black trim (pillars, louvres, mirror arms)
const GRILLE = "#232529"; // dark grille mesh behind the chrome ring
const WELL = "#141518"; // wheel-well discs
const GLASS = "#26333f"; // tinted side / rear glazing
const SCREEN = "#374d61"; // windscreen (a shade lighter than the tint)
const CABIN = "#131418"; // near-black interior block behind the glass (seals the cabin)
const TIRE = "#1a1b1d";
const SPOKE = "#c7ccd1"; // bright wire spokes / rim
const LAMP = "#eef4f8"; // headlamp glass
const RED = "#a51525"; // tail lamps
const REDDARK = "#6f1019"; // tail-lamp inner
const AMBER = "#e8a13a";
const INT = "#7c2020"; // red interior hint through the glass
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.33;
const TYRE_W = 0.19;
const WHEEL_X = 0.79; // half track — tyre outer face proud of the fender bulge
const AXLE_F = 1.15;
const AXLE_R = -1.25; // wheelbase 2.40
const BODY_X = 0.82; // main body half-width

// Continuous fastback roofline (mirrors porsche-911.ts): the roof peak descends
// in three overlapping tilted boxes whose top surfaces meet on shared
// breakpoints, so there is NO flat deck behind the cabin — one unbroken,
// near-uniform teardrop slope from the peak to a low tail. Breakpoints (z, y):
// (-0.10, 1.10) -> (-0.70, 0.97) -> (-1.30, 0.85) -> (-1.90, 0.73).
const SAIL_ANGLE = 0.213; // rear-window drop
const LID_ANGLE = 0.197;
const DECK_ANGLE = 0.197; // still descending onto the low tail (never flat)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the curvaceous fender haunches, the rounded nose and the tail cap. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One wire wheel: dark tyre, a bright rim ring, twelve thin bright spokes
 *  proud of the tyre's outer cap, a chrome hub and a knock-off spinner cone.
 *  Every part shares `name` so the `drive` track spins them as one node about
 *  the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const outer = WHEEL_X + TYRE_W / 2; // tyre outer cap at 0.885 — spokes ride just proud of it
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    // Bright rim ring, just proud of the outer cap (the wire-wheel lip).
    {
      geometry: new THREE.TorusGeometry(0.29, 0.024, 6, 22),
      color: SPOKE,
      position: [side * (outer + 0.012), y, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      name,
    },
  ];
  for (let k = 0; k < 12; k++) {
    parts.push(
      box(0.016, 0.56, 0.016, SPOKE, [side * (outer + 0.008), y, z], [(k * Math.PI) / 12, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.075, 0.075, 0.05, 14, "#b7bcc2", [side * (outer + 0.02), y, z], [0, 0, Math.PI / 2], { name })); // hub
  parts.push({ // knock-off spinner cone pointing outward
    geometry: new THREE.ConeGeometry(0.055, 0.09, 12),
    color: CHROME,
    position: [side * (outer + 0.06), y, z] as Vec3,
    rotation: [0, 0, -side * Math.PI / 2] as Vec3,
    name,
  });
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus the
 *  signature 300SL CHROME eyebrow brow — an upper half-torus over the tyre.
 *  The [0, pi/2, 0] yaw puts the 0..pi arc exactly over the top half. The
 *  tyre's outer cap (0.885) stays proud of both. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.42, 0.42, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.40, 0.032, 6, 20, Math.PI),
      color: CHROME,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Fronts are amber chips inboard of the headlamps; rears are amber chips beside
// the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.5, 0.5, 2.14], size: [0.11, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.5, 0.5, 2.14], size: [0.11, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.5, 0.54, -2.08], size: [0.09, 0.05, 0.04] },
  { name: "indicator-rr", pos: [0.5, 0.54, -2.08], size: [0.09, 0.05, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.12, 3.9, TRIM, [0, 0.28, -0.05])); // underbody pan

  // -- Body masses (paint) ----------------------------------------------------
  P.push(paint(1.6, 0.30, 3.9, [0, 0.47, -0.06])); // lower tub, sill to belt
  P.push(paint(1.6, 0.22, 1.5, [0, 0.70, -0.1])); // upper body band, cowl to behind the cabin (kept LOW so the fastback boxes are always the top)
  // Rear fender haunches — the rounded 300SL hip over the rear wheels, carried
  // back to fill and round the rear flanks under the descending fastback.
  for (const s of S) P.push(blob([0.11, 0.2, 0.72], [s * 0.79, 0.6, -1.4]));
  // Front fender crowns — high fender line beside the low bonnet, cresting over
  // the faired headlamps.
  for (const s of S) P.push(paint(0.3, 0.15, 1.72, [s * 0.66, 0.80, 1.16], [0.07, 0, 0]));

  // Long curved bonnet dipping between the fender crowns to the nose (extends
  // well back to the scuttle — a LONG bonnet with a SHORT boot, 300SL-style).
  P.push(paint(1.14, 0.15, 1.72, [0, 0.77, 1.16], [0.10, 0, 0]));
  // Twin power bulges — two low ridges running the bonnet length (iconic).
  for (const s of S) P.push(paint(0.32, 0.08, 1.6, [s * 0.19, 0.85, 1.18], [0.10, 0, 0]));

  // Nose and tail — modest rounded caps, the grille (not the bodywork) frontmost.
  P.push(paint(1.46, 0.5, 0.3, [0, 0.6, 1.95])); // nose block behind the grille (front face z 2.10)
  P.push(blob([0.7, 0.14, 0.14], [0, 0.4, 2.05])); // soft lower chin
  P.push(paint(1.5, 0.42, 0.24, [0, 0.54, -1.93])); // low tail panel (top ~0.75 — the fastback flows down into it)
  P.push(blob([0.68, 0.06, 0.15], [0, 0.72, -1.92])); // small rounded lip where the deck meets the tail

  // Fill the volume under the bumpers back to the wheel wells (no see-through).
  P.push(box(1.28, 0.22, 0.3, TRIM, [0, 0.33, 1.98])); // front valance
  P.push(box(1.28, 0.24, 0.3, TRIM, [0, 0.34, -1.92])); // rear valance

  // -- The unbroken fastback: sail + lid + tail deck (mirrors porsche-911) -----
  // Three tilted paint boxes whose top surfaces meet on the shared breakpoints,
  // each running a little past its span so the ends overlap (never butt). This
  // is the WHOLE rear: the roof peak drops steeply (the sail / rear window),
  // then flattens onto the low tail in ONE continuous line — there is NO flat
  // horizontal deck behind the cabin. The boxes are tall enough to also form the
  // rear body sides down to the tub.
  P.push(paint(1.32, 0.42, 0.72, [0, 0.83, -0.40], [-SAIL_ANGLE, 0, 0])); // sail: rear-window drop
  P.push(paint(1.44, 0.30, 0.72, [0, 0.763, -1.00], [-LID_ANGLE, 0, 0])); // lid: continuing the slope
  P.push(paint(1.50, 0.26, 0.72, [0, 0.663, -1.60], [-DECK_ANGLE, 0, 0])); // tail deck: still descending onto the low tail
  P.push(box(1.12, 0.04, 0.88, GLASS, [0, 0.98, -0.62], [-LID_ANGLE, 0, 0])); // large fastback backlight lying ON the sail/lid
  P.push(cyl(0.03, 0.03, 0.016, 12, CHROME, [0, 0.79, -1.5], [-DECK_ANGLE, 0, 0])); // boot badge disc on the deck

  // -- Greenhouse: a LOW glasshouse whose roof begins sloping right behind the
  // doors — a teardrop, not a raised box. Windscreen + short low roof peak +
  // door glass + a rear quarter window on the fastback flank (so the glasshouse
  // reads as extending back, not a solid deck). A dark interior block seals it.
  P.push(box(1.04, 0.03, 0.1, TRIM, [0, 0.81, 0.54])); // cowl vent at the windscreen base
  P.push(box(1.02, 0.24, 0.74, CABIN, [0, 0.92, 0.06])); // dark interior block sealing the cabin (z 0.43..-0.31)
  P.push(box(0.82, 0.06, 0.4, INT, [0, 0.83, 0.14])); // low red seat hint glimpsed through the glass
  P.push(box(1.16, 0.42, 0.05, SCREEN, [0, 0.94, 0.42], [-0.5, 0, 0])); // raked wraparound windscreen
  for (const s of S) P.push(paint(0.09, 0.42, 0.09, [s * 0.58, 0.94, 0.42], [-0.5, 0, 0])); // A-pillars
  P.push(paint(1.24, 0.07, 0.42, [0, 1.10, 0.14])); // short LOW roof peak over the seats (WIDER than the glass)
  for (const s of S) {
    P.push(box(0.03, 0.22, 0.5, GLASS, [s * 0.56, 0.94, 0.12], [0, 0, s * 0.16])); // door glass, tumblehome in
    P.push(box(0.03, 0.15, 1.3, GLASS, [s * 0.6, 0.86, -0.75], [-0.19, 0, s * 0.06])); // quarter window pitched to FOLLOW the fastback down the flank (continuous glass to the tail in profile)
    P.push(box(0.02, 0.03, 0.55, CHROME, [s * 0.575, 1.06, 0.12], [0, 0, s * 0.16])); // bright chrome roof-rail frame
    P.push(box(0.02, 0.03, 0.55, CHROME, [s * 0.585, 0.83, 0.12])); // bright chrome belt frame under the glass
  }

  // -- Front fascia: huge chrome oval grille + central three-point star -------
  // Grille mesh (dark), then a wide chrome oval ring, then the star proud of both.
  P.push(box(0.86, 0.58, 0.04, GRILLE, [0, 0.62, 2.06])); // dark grille backing
  for (let i = -1; i <= 1; i++) P.push(box(0.82, 0.02, 0.03, CHROME, [0, 0.62 + i * 0.18, 2.08])); // horizontal chrome bars
  // The chrome oval surround — a torus scaled wider than tall (faces +Z; no
  // yaw, so the non-uniform scale never shears it).
  P.push({
    geometry: new THREE.TorusGeometry(0.36, 0.032, 8, 28),
    color: CHROME,
    position: [0, 0.62, 2.09] as Vec3,
    scale: [1.15, 0.82, 1] as Vec3,
  });
  // Central three-point star: chrome ring, hub, three arms (one up, two lower).
  P.push({ geometry: new THREE.TorusGeometry(0.14, 0.018, 8, 24), color: CHROME, position: [0, 0.62, 2.12] as Vec3 });
  P.push(cyl(0.05, 0.05, 0.03, 14, CHROME, [0, 0.62, 2.14], [Math.PI / 2, 0, 0])); // star hub
  for (let k = 0; k < 3; k++) {
    const th = (k * 2 * Math.PI) / 3;
    const off: Vec3 = [-Math.sin(th) * 0.08, 0.62 + Math.cos(th) * 0.08, 2.13];
    P.push(box(0.028, 0.16, 0.02, CHROME, off, [0, 0, th]));
  }

  // Headlamps faired into the fender fronts (vertical oval lamp faces under a
  // chrome bezel), plus small round driving lamps inboard beside the grille.
  for (const s of S) {
    P.push(cyl(0.14, 0.14, 0.1, 18, PAINT, [s * 0.63, 0.68, 1.98], [Math.PI / 2, 0, 0], { slot: "paint" })); // faired pod
    P.push({ geometry: new THREE.CylinderGeometry(0.12, 0.12, 0.03, 18), color: CHROME, position: [s * 0.63, 0.68, 2.04] as Vec3, rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [0.72, 1, 1] as Vec3 }); // oval bezel
    P.push({ geometry: new THREE.CylinderGeometry(0.1, 0.1, 0.02, 18), color: LAMP, position: [s * 0.63, 0.68, 2.055] as Vec3, rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [0.72, 1, 1] as Vec3 }); // oval glass
    P.push(cyl(0.05, 0.05, 0.03, 14, CHROME, [s * 0.32, 0.46, 2.14], [Math.PI / 2, 0, 0])); // driving lamp bezel
    P.push(cyl(0.04, 0.04, 0.02, 14, LAMP, [s * 0.32, 0.46, 2.16], [Math.PI / 2, 0, 0])); // driving lamp glass
  }

  // Chrome front bumper: a blade with two overriders and a centre bar.
  P.push(box(1.3, 0.08, 0.08, CHROME, [0, 0.36, 2.12]));
  for (const s of S) P.push(box(0.09, 0.2, 0.12, CHROME, [s * 0.4, 0.4, 2.14])); // overrider bombs
  P.push(box(0.06, 0.22, 0.1, CHROME, [0, 0.42, 2.13])); // centre bar
  P.push(box(0.34, 0.11, 0.02, PLATE, [0, 0.32, 2.17])); // front plate

  // -- Fender gills: louvred chrome vents behind the front wheel arch ---------
  for (const s of S) {
    P.push(box(0.03, 0.16, 0.34, TRIM, [s * 0.82, 0.66, 0.72])); // dark vent backing
    for (let i = 0; i < 3; i++) {
      P.push(box(0.04, 0.14, 0.03, CHROME, [s * 0.835, 0.66, 0.83 - i * 0.11], [0, s * 0.25, 0])); // angled chrome fins
    }
  }

  // -- Rear fascia -------------------------------------------------------------
  for (const s of S) {
    // small vertical tail lamps at the rear corners, proud of the tail panel
    P.push(box(0.1, 0.22, 0.06, REDDARK, [s * 0.62, 0.64, -2.02])); // lamp base
    P.push(box(0.08, 0.18, 0.05, RED, [s * 0.62, 0.64, -2.05])); // red lens, proud of the base
  }
  P.push(box(1.3, 0.08, 0.08, CHROME, [0, 0.4, -2.05])); // rear bumper blade
  for (const s of S) P.push(box(0.09, 0.2, 0.12, CHROME, [s * 0.4, 0.44, -2.07])); // overrider bombs
  P.push(box(0.06, 0.22, 0.1, CHROME, [0, 0.46, -2.06])); // centre bar
  P.push(box(0.34, 0.11, 0.02, PLATE, [0, 0.4, -2.1])); // rear plate
  P.push(cyl(0.032, 0.032, 0.16, 12, CHROME, [0.5, 0.28, -2.05], [Math.PI / 2, 0, 0])); // discreet exhaust

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.02, 0.02, TRIM, [s * 0.82, 0.85, 0.34])); // mirror arm
    P.push(cyl(0.045, 0.045, 0.02, 12, CHROME, [s * 0.855, 0.87, 0.34], [0, 0, Math.PI / 2])); // mirror head
    P.push(box(0.02, 0.03, 0.11, CHROME, [s * 0.83, 0.63, 0.05])); // door handle
    P.push(box(0.02, 0.028, 2.0, CHROME, [s * 0.83, 0.6, -0.2])); // chrome side spear along the flank (300SL trim; breaks the slab)
    P.push(box(0.26, 0.012, 0.03, TRIM, [s * 0.24, 0.79, 0.52], [-0.5, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const mercedes300sl = defineModel({
  id: "mercedes-300sl",
  name: "Mercedes 300 SL",
  batchId: "cars",
  tags: ["car", "mercedes", "300sl", "gullwing", "coupe", "sports", "classic"],
  summary: "A 1954 Mercedes-Benz 300 SL Gullwing sports coupe at true scale, in classic silver.",
  description:
    "A 1954 Mercedes-Benz 300 SL 'Gullwing' (W198) sports coupe at real 1 unit " +
    "= 1 metre scale (about 4.52 long, 1.79 wide, 1.30 tall, 2.40 wheelbase, on " +
    "wire wheels). A low, wide, curvaceous silver coupe: a long curved bonnet " +
    "with twin power bulges dipping between high fender crowns, a huge chrome " +
    "oval grille dominated by a central three-point star, round headlamps " +
    "faired into the fender fronts with small driving lamps, a small wraparound " +
    "greenhouse set well back over a short fastback tail, the signature chrome " +
    "eyebrow brows arching over every wheel arch, louvred chrome gills behind " +
    "the front wheels, chrome bumpers with overriders and a centre bar front " +
    "and rear, small round tail lamps, a discreet exhaust and a red interior " +
    "hint through the glass. Body panels carry the 'paint' recolour slot " +
    "(classic silver); `drive` spins the wire wheels and `hazards` blinks the " +
    "four indicators.",
  author: "Fable 5",
  footprint: 2.4,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
